import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Image,
  Modal,
  Pressable,
  Alert,
} from 'react-native';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { BottomNavBar } from '../../components/BottomNavBar';
import { profileLogger } from '../../utils/logger';
import { analytics } from '../../services/analytics';
import { wipeSessionOnLogout } from '../../services/auth/logoutWipe';
import { getCachedProfileImage, removeCachedProfileImage, setCachedProfileImage } from '../../services/profileImageCache';

// Import default profile image
import defaultProfileImage from '../../assets/icons/profile.png';

// Import icons
import userIcon from '../../assets/icons/user.png';
import settingsIcon from '../../assets/icons/settings.png';
import secretCodeIcon from '../../assets/icons/lock.png';
import successorIcon from '../../assets/icons/successor.png';
import keyHolderIcon from '../../assets/icons/key-holder.png';
import chevronIcon from '../../assets/icons/chevron.png';
import infoIcon from '../../assets/icons/info.png';
import closeIcon from '../../assets/icons/close.png';
import notificationsIcon from '../../assets/icons/notifications.png';
import linkedAccountsIcon from '../../assets/icons/linked-accounts.png';
import familyIcon from '../../assets/icons/family.png';
import logo from '../../assets/logo.png';
import logoOrange from '../../assets/logo-orange.png';
import logoWhite from '../../assets/logo_white.png';
import tasksIcon from '../../assets/icons/tasks.png';
import docsIcon from '../../assets/icons/docs.png';
import dashboardIcon from '../../assets/icons/dashboard.png';
import { ms, s, vs } from '../../utils/responsive';

interface ProfileScreenProps {
  navigation: NavigationProp<any>;
}

const ProfileScreen = ({ navigation }: ProfileScreenProps) => {
  const [showSuccessorInfo, setShowSuccessorInfo] = useState(false);
  const [showKeyHolderInfo, setShowKeyHolderInfo] = useState(false);
  const [showSecretCodeInfo, setShowSecretCodeInfo] = useState(false);
  const [userName, setUserName] = useState('Loading...');
  const [userEmail, setUserEmail] = useState('Loading...');
  const [isSuccessor, setIsSuccessor] = useState(false);
  const [primaryUsers, setPrimaryUsers] = useState<any[]>([]);
  const [profileImageUri, setProfileImageUri] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Load user data when screen focuses
  useFocusEffect(
    React.useCallback(() => {
      loadUserData();
      checkIfSuccessor();
      loadProfileImage();
    }, [])
  );

  const loadProfileImage = async () => {
    try {
      const token = await getAuthToken();

      // Try to load from backend first
      if (token) {
        try {
          const response = await fetch(`${API_URL}/users/profile-image`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.imageData) {
              await setCachedProfileImage(data.imageData);
              setProfileImageUri(data.imageData);
              return;
            }

            if (data.success) {
              await removeCachedProfileImage();
              setProfileImageUri(null);
              return;
            }
          }
        } catch (backendError) {
          profileLogger.debug('Could not load from backend, trying local cache:', backendError);
        }
      }

      const savedImageUri = await getCachedProfileImage();
      setProfileImageUri(savedImageUri);
    } catch (error) {
      profileLogger.error('Error loading profile image:', error);
    }
  };

  const loadUserData = async () => {
    try {
      // Get the auth token
      const token = await getAuthToken();
      if (!token) {
        profileLogger.error('No auth token found');
        setUserName('User Name');
        setUserEmail('user@example.com');
        return;
      }

      // Fetch current user profile from the backend
      const response = await fetch(`${API_URL}/users/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        profileLogger.debug('User profile data:', result);

        // Extract user data from the response
        // The backend should return user info, let's check what we get
        if (result.user) {
          const fullName = `${result.user.firstName || ''} ${result.user.lastName || ''}`.trim();
          setUserName(fullName || 'User Name');
          setUserEmail(result.user.email || 'user@example.com');
        }
      } else {
        profileLogger.error('Failed to fetch user profile');
        // Fallback to AsyncStorage data
        const userDataString = await AsyncStorage.getItem('user');
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          setUserName(fullName || 'User Name');
          setUserEmail(userData.email || 'user@example.com');
        }
      }
    } catch (error) {
      profileLogger.error('Error loading user data:', error);
      setUserName('User Name');
      setUserEmail('user@example.com');
    }
  };

  const checkIfSuccessor = async () => {
    try {
      const token = await getAuthToken();
      if (!token) return;

      // Check if user has any primary users (is a successor to someone)
      const response = await fetch(`${API_URL}/api/relationships/primary-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        const successors = (result.primaryUsers || []).filter((u: any) => u.role === 'successor');
        setIsSuccessor(successors.length > 0);
        setPrimaryUsers(successors);
      }
    } catch (error) {
      profileLogger.error('Error checking successor status:', error);
    }
  };

  const handleLogout = async () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Logout', 
          style: 'destructive',
          onPress: async () => {
            try {
              // Deregister push notifications
              try {
                const { pushNotificationService } = require('../../services/pushNotificationService');
                await pushNotificationService.removeToken();
              } catch (e) {
                console.log('[Push] Token removal on logout failed (non-fatal):', e);
              }

              // Centralised wipe: keychain tokens, session AsyncStorage,
              // profile-image caches, errorLoggingService buffer, and
              // in-memory caches. Preserves the per-user salt + biometric
              // credential so re-login works.
              await wipeSessionOnLogout();
              // Ensure the user is taken to Login screen, not SignUp
              await AsyncStorage.setItem('@email_verified', 'true');
              await AsyncStorage.setItem('@2fa_setup_complete', 'true');

              analytics.reset();

              profileLogger.debug('Logout complete, navigating to Login');

              // Navigate directly to Login screen instead of going through Splash
              navigation.reset({
                index: 0,
                routes: [{
                  name: 'Auth',
                  state: {
                    routes: [{ name: 'Login' }]
                  }
                }],
              });
            } catch (error) {
              profileLogger.error('Error during logout:', error);
              Alert.alert('Error', 'Failed to logout properly');
            }
          }
        }
      ]
    );
  };

  const menuItems = [
    {
      id: 'editProfile',
      title: 'Edit Profile',
      icon: userIcon,
      screen: 'EditProfile',
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: settingsIcon,
      screen: 'Settings',
    },
    // Family Plan lived only under Settings, where an owner could not find
    // where to invite (2026-09-22). Everyone sees it: members see their
    // plan, owners invite, others see what the family plans include.
    {
      id: 'familyPlan',
      title: 'Family Plan',
      icon: familyIcon,
      screen: 'FamilyPlan',
    },
    // Show Request Full Access only for successors
    ...(isSuccessor ? [{
      id: 'requestFullAccess',
      title: 'Request Full Access',
      icon: secretCodeIcon,
      screen: 'RequestFullAccess',
    }] : []),
    {
      id: 'successor',
      title: 'Add Successor',
      icon: successorIcon,
      screen: 'AddSuccessor',
      showInfo: true,
    },
    {
      id: 'keyHolder',
      title: 'Add Key Holder',
      icon: keyHolderIcon,
      screen: 'AddKeyholder',
      showInfo: true,
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: notificationsIcon,
      screen: 'Notifications',
    },
    {
      id: 'linkedAccounts',
      title: 'Linked Accounts',
      icon: linkedAccountsIcon,
      screen: 'LinkedAccounts',
    },
  ];

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.logoContainer}>
        <TouchableOpacity style={styles.hamburgerButton} onPress={() => setMenuOpen(true)}>
          <View style={styles.hamburgerBar} />
          <View style={styles.hamburgerBar} />
          <View style={styles.hamburgerBar} />
        </TouchableOpacity>
        <Image source={logo} style={styles.logo} resizeMode="contain" />
      </View>
      <View style={styles.header}>
        <View style={styles.profileSection}>
          <View style={styles.profileImageContainer}>
            <Image
              source={profileImageUri ? { uri: profileImageUri } : defaultProfileImage}
              style={styles.profileImage}
            />
          </View>
          <Text style={styles.userName}>{userName}</Text>
          <Text style={styles.userEmail}>{userEmail}</Text>
        </View>
      </View>

      <View style={styles.menuContainer}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
        >
          {menuItems.map((item) => (
            <TouchableOpacity
              key={item.id}
              style={styles.menuItem}
              onPress={() => navigation.navigate(item.screen)}
            >
              <View style={styles.menuItemLeft}>
                <Image 
                  source={item.icon} 
                  style={[styles.menuItemIcon, { tintColor: '#29BFC0' }]}
                />
                <Text style={styles.menuItemText}>{item.title}</Text>
              </View>
              <View style={styles.menuItemRight}>
                {item.showInfo && item.id === 'successor' && (
                  <TouchableOpacity 
                    onPress={() => setShowSuccessorInfo(true)}
                    style={styles.infoButton}
                  >
                    <Image source={infoIcon} style={[styles.menuItemIcon, { tintColor: '#999' }]} />
                  </TouchableOpacity>
                )}
                {item.showInfo && item.id === 'keyHolder' && (
                  <TouchableOpacity 
                    onPress={() => setShowKeyHolderInfo(true)}
                    style={styles.infoButton}
                  >
                    <Image source={infoIcon} style={[styles.menuItemIcon, { tintColor: '#999' }]} />
                  </TouchableOpacity>
                )}
                {item.showInfo && item.id === 'secretCode' && (
                  <TouchableOpacity 
                    onPress={() => setShowSecretCodeInfo(true)}
                    style={styles.infoButton}
                  >
                    <Image source={infoIcon} style={[styles.menuItemIcon, { tintColor: '#999' }]} />
                  </TouchableOpacity>
                )}
                <Image source={chevronIcon} style={styles.menuItemArrow} />
              </View>
            </TouchableOpacity>
          ))}
          
          {/* Logout Button */}
          <TouchableOpacity 
            style={styles.logoutButton}
            onPress={handleLogout}
            activeOpacity={0.7}
          >
            <Text style={styles.logoutButtonText}>Logout</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>

      <BottomNavBar />

      <Modal
        visible={showSuccessorInfo}
        transparent={true}
        animationType="fade"
        statusBarTranslucent={true}
        onRequestClose={() => setShowSuccessorInfo(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setShowSuccessorInfo(false)}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Who is the Successor?</Text>
              <TouchableOpacity 
                onPress={() => setShowSuccessorInfo(false)}
                style={styles.closeButton}
              >
                <Image source={closeIcon} style={styles.closeIcon} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.modalScroll}
              contentContainerStyle={styles.modalScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <Text style={styles.modalText}>
                The Successor is the designated individual entrusted with full access to your critical information in the event of your passing or incapacitation. They act as the executor of your stored data, ensuring that all essential details—such as financial records, legal documents, and personal instructions—are securely processed and handled according to your wishes. The Successor holds the highest level of authority within the app, allowing them to manage, distribute, or update the stored data as needed, providing peace of mind and continuity for your loved ones.
              </Text>

              <Text style={styles.modalSectionTitle}>
                How does my Successor gain access to my information?
              </Text>
              <Text style={styles.modalText}>
                When setting up your profile, you will have the option to designate a Successor in the profile settings of the app. You provide them with a secret passcode that grants access to your data. You can choose to notify them via email or text message.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can I change my Successor?
              </Text>
              <Text style={styles.modalText}>
                Yes, you can change your Successor at any time through the profile settings. When you update your Successor, the previous Successor's access will be revoked, and a new passcode will be generated.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can I control what information my Successor has access to?
              </Text>
              <Text style={styles.modalText}>
                Yes, you can customize the level of access and specific information available to your Successor through the app's security settings.
              </Text>

              <Text style={styles.modalSectionTitle}>
                What happens if my Successor loses the secret passcode?
              </Text>
              <Text style={styles.modalText}>
                If your Successor loses their passcode, they can request a reset through the app. You will be notified and can approve the reset request.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can I have more than one Successor?
              </Text>
              <Text style={styles.modalText}>
                Currently, you can only designate one primary Successor at a time to ensure clear authority and prevent potential conflicts.
              </Text>

              <Text style={styles.modalSectionTitle}>
                What is the difference between a Successor and a Key Holder?
              </Text>
              <Text style={styles.modalText}>
                A Successor has full access and authority over your stored information, while a Key Holder has limited access to specific data you choose to share.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Is my data secure with a Successor?
              </Text>
              <Text style={styles.modalText}>
                Yes, all data shared with your Successor is encrypted and can only be accessed with the proper credentials. The app maintains detailed logs of all access.
              </Text>

              <Text style={styles.modalSectionTitle}>
                How do I notify my Successor about their role?
              </Text>
              <Text style={styles.modalText}>
                When you designate a Successor, you can choose to notify them via email or text message. They will receive instructions on their responsibilities and how to access information when needed.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can my Successor decline their role?
              </Text>
              <Text style={styles.modalText}>
                Yes, a designated Successor can decline their role through the app. You will be notified if this occurs and can then designate a new Successor.
              </Text>

              <Text style={styles.modalSectionTitle}>
                What happens if I do not assign a Successor?
              </Text>
              <Text style={styles.modalText}>
                Without a designated Successor, there may be challenges in accessing and managing your digital assets and information in case of emergency.
              </Text>
            </ScrollView>
            <TouchableOpacity 
              style={styles.backButton}
              onPress={() => setShowSuccessorInfo(false)}
            >
              <Text style={styles.backButtonText}>Back to Successor Details</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showKeyHolderInfo}
        transparent={true}
        animationType="fade"
        statusBarTranslucent={true}
        onRequestClose={() => setShowKeyHolderInfo(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setShowKeyHolderInfo(false)}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Who is the Key Holder?</Text>
              <TouchableOpacity 
                onPress={() => setShowKeyHolderInfo(false)}
                style={styles.closeButton}
              >
                <Image source={closeIcon} style={styles.closeIcon} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.modalScroll}
              contentContainerStyle={styles.modalScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <Text style={styles.modalText}>
                A Key Holder is a trusted individual who has limited access to specific information you choose to share. Unlike a Successor, who has full access to your data, a Key Holder can only view and manage the particular data sets you explicitly grant them access to. This role is perfect for sharing specific documents or information while maintaining privacy for other sensitive data.
              </Text>

              <Text style={styles.modalSectionTitle}>
                How does my Key Holder gain access to my information?
              </Text>
              <Text style={styles.modalText}>
                When setting up your profile, you can designate Key Holders in the profile settings. You'll provide them with specific access codes for the information you want to share. You can notify them via email or text message about their role and access.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can I change my Key Holders?
              </Text>
              <Text style={styles.modalText}>
                Yes, you can add, remove, or modify Key Holders at any time through the profile settings. When you update a Key Holder's access, their previous access codes will be revoked, and new ones will be generated if needed.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can I control what information my Key Holders have access to?
              </Text>
              <Text style={styles.modalText}>
                Yes, you have complete control over what information each Key Holder can access. You can customize access levels for each Key Holder individually, granting or restricting access to specific documents or data categories.
              </Text>

              <Text style={styles.modalSectionTitle}>
                What happens if my Key Holder loses their access code?
              </Text>
              <Text style={styles.modalText}>
                If a Key Holder loses their access code, they can request a reset through the app. You will be notified and can approve the reset request for their specific access permissions.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can I have multiple Key Holders?
              </Text>
              <Text style={styles.modalText}>
                Yes, you can have multiple Key Holders, each with their own specific access permissions. This allows you to share different information with different trusted individuals based on their needs and roles.
              </Text>

              <Text style={styles.modalSectionTitle}>
                What is the difference between a Key Holder and a Successor?
              </Text>
              <Text style={styles.modalText}>
                While a Successor has full access to all your stored information and can manage your digital legacy, a Key Holder has limited access only to specific information you choose to share. Key Holders are ideal for sharing selective information while maintaining privacy for other data.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Is my data secure with Key Holders?
              </Text>
              <Text style={styles.modalText}>
                Yes, all data shared with Key Holders is encrypted and can only be accessed with the proper credentials. The app maintains detailed logs of all access attempts and successful views of your information.
              </Text>

              <Text style={styles.modalSectionTitle}>
                How do I notify my Key Holders about their role?
              </Text>
              <Text style={styles.modalText}>
                When you designate a Key Holder, you can notify them via email or text message. They will receive instructions about their specific access permissions and how to view the information you've shared with them.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can Key Holders decline their role?
              </Text>
              <Text style={styles.modalText}>
                Yes, designated Key Holders can decline their role through the app. You will be notified if this occurs, allowing you to designate a different Key Holder if needed.
              </Text>

              <Text style={styles.modalSectionTitle}>
                What happens if I don't assign any Key Holders?
              </Text>
              <Text style={styles.modalText}>
                Not having Key Holders is perfectly fine - it's an optional feature. You can always add Key Holders later when you need to share specific information with trusted individuals.
              </Text>
            </ScrollView>
            <TouchableOpacity 
              style={styles.backButton}
              onPress={() => setShowKeyHolderInfo(false)}
            >
              <Text style={styles.backButtonText}>Back to Key Holder Details</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      <Modal
        visible={showSecretCodeInfo}
        transparent={true}
        animationType="fade"
        statusBarTranslucent={true}
        onRequestClose={() => setShowSecretCodeInfo(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setShowSecretCodeInfo(false)}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>What is the Secret Code?</Text>
              <TouchableOpacity 
                onPress={() => setShowSecretCodeInfo(false)}
                style={styles.closeButton}
              >
                <Image source={closeIcon} style={styles.closeIcon} />
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.modalScroll}
              contentContainerStyle={styles.modalScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <Text style={styles.modalText}>
                The Secret Code is a unique, secure passcode that provides an additional layer of protection for your sensitive information. This code is separate from your regular login password and acts as a master key for accessing your most critical data.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Why do I need a Secret Code?
              </Text>
              <Text style={styles.modalText}>
                Your Secret Code adds an extra layer of security to protect your most sensitive information. It ensures that even if someone gains access to your account, they cannot access your critical data without this additional code.
              </Text>

              <Text style={styles.modalSectionTitle}>
                How should I create my Secret Code?
              </Text>
              <Text style={styles.modalText}>
                Your Secret Code should be:
                • At least 8 characters long
                • Include a mix of numbers, letters, and special characters
                • Different from your login password
                • Something memorable but not easily guessable
              </Text>

              <Text style={styles.modalSectionTitle}>
                What happens if I forget my Secret Code?
              </Text>
              <Text style={styles.modalText}>
                If you forget your Secret Code, you can reset it through a secure recovery process. This may involve verifying your identity through multiple authentication methods and potentially contacting support for assistance.
              </Text>

              <Text style={styles.modalSectionTitle}>
                How often should I change my Secret Code?
              </Text>
              <Text style={styles.modalText}>
                We recommend changing your Secret Code every 3-6 months for optimal security. The app will remind you when it's time to update your code.
              </Text>

              <Text style={styles.modalSectionTitle}>
                Can I share my Secret Code?
              </Text>
              <Text style={styles.modalText}>
                Your Secret Code should never be shared with anyone. If you need to give someone access to specific information, use the Key Holder feature instead, which allows for controlled, limited access.
              </Text>

              <Text style={styles.modalSectionTitle}>
                How is my Secret Code protected?
              </Text>
              <Text style={styles.modalText}>
                Your Secret Code is encrypted using advanced security protocols. It is never stored in plain text and cannot be viewed by anyone, including our support team.
              </Text>
            </ScrollView>
            <TouchableOpacity 
              style={styles.backButton}
              onPress={() => setShowSecretCodeInfo(false)}
            >
              <Text style={styles.backButtonText}>Back to Secret Code Details</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Side Menu Overlay */}
      {menuOpen && (
        <TouchableOpacity style={styles.menuOverlay} activeOpacity={1} onPress={() => setMenuOpen(false)}>
          <View style={styles.sideMenu}>
            <Image source={logoWhite} style={styles.menuLogo} resizeMode="contain" />
            <TouchableOpacity onPress={() => setMenuOpen(false)} style={styles.closeMenuButton}>
              <Text style={styles.closeMenuText}>×</Text>
            </TouchableOpacity>
            <View style={styles.menuLinksContainer}>
              <TouchableOpacity style={styles.menuLink} onPress={() => { setMenuOpen(false); navigation.navigate('EditProfile'); }}>
                <Image source={userIcon} style={styles.menuIconTeal} />
                <Text style={styles.sideMenuText}>Edit Profile</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.menuLink} onPress={() => { setMenuOpen(false); navigation.navigate('AllTasks' as never); }}>
                <Image source={tasksIcon} style={styles.menuIconTeal} />
                <Text style={styles.sideMenuText}>All Tasks</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.menuLink} onPress={() => { setMenuOpen(false); navigation.navigate('Documents' as never); }}>
                <Image source={docsIcon} style={styles.menuIconTeal} />
                <Text style={styles.sideMenuText}>My Documents</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.menuLink} onPress={() => { setMenuOpen(false); navigation.reset({ index: 0, routes: [{ name: 'Dashboard' as never }] }); }}>
                <Image source={dashboardIcon} style={styles.menuIconTeal} />
                <Text style={styles.sideMenuText}>Dashboard</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  logoContainer: {
    alignItems: 'center',
    paddingTop: ms(40),
    paddingBottom: ms(10),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  header: {
    paddingTop: ms(20),
    paddingHorizontal: ms(20),
    backgroundColor: '#F3F6F8',
  },
  profileSection: {
    alignItems: 'center',
    marginBottom: ms(20),
  },
  profileImageContainer: {
    width: s(100),
    height: s(100),
    borderRadius: ms(50),
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: ms(10),
    overflow: 'hidden',
  },
  profileImage: {
    width: s(100),
    height: s(100),
    borderRadius: ms(50),
  },
  userName: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(5),
  },
  userEmail: {
    fontSize: ms(16, 0.3),
    color: '#666',
  },
  menuContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: ms(20),
    paddingBottom: ms(100),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    padding: ms(15),
    borderRadius: ms(15),
    marginBottom: ms(15),
    paddingHorizontal: ms(20),
    borderWidth: 1,
    borderColor: '#d8dbdd',
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuItemIcon: {
    width: s(24),
    height: s(24),
    marginRight: ms(15),
  },
  menuItemText: {
    fontSize: ms(16, 0.3),
    color: '#333333',
  },
  menuItemArrow: {
    width: s(24),
    height: s(24),
    tintColor: '#333333',
  },
  menuItemRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoButton: {
    marginRight: ms(10),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(20),
    paddingVertical: ms(24),
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    width: '100%',
    maxWidth: s(420),
    maxHeight: '82%',
    padding: ms(20),
    flexShrink: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  closeButton: {
    padding: ms(5),
  },
  closeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#333',
  },
  modalScroll: {
    flexGrow: 0,
    flexShrink: 1,
    marginBottom: ms(15),
  },
  modalScrollContent: {
    paddingBottom: ms(4),
  },
  modalText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(15),
  },
  modalSectionTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(10),
    marginTop: ms(5),
  },
  backButton: {
    backgroundColor: '#29BFC0',
    height: s(50),
    borderRadius: ms(25),
    alignItems: 'center',
    justifyContent: 'center',
  },
  backButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  logoutButton: {
    backgroundColor: '#FF6B6B',
    borderRadius: ms(15),
    padding: ms(15),
    marginTop: ms(20),
    marginBottom: ms(20),
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#FF6B6B',
  },
  logoutButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  hamburgerButton: {
    position: 'absolute',
    left: ms(10),
    top: ms(35),
    zIndex: 20,
    width: s(40),
    height: s(40),
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  hamburgerBar: {
    width: s(28),
    height: s(4),
    backgroundColor: '#1C2C4F',
    borderRadius: ms(2),
    marginVertical: ms(2),
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.2)',
    zIndex: 1000,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  sideMenu: {
    width: s(260),
    height: '98%',
    backgroundColor: '#1C2C4F',
    borderTopRightRadius: ms(30),
    borderBottomRightRadius: ms(30),
    paddingTop: ms(40),
    paddingHorizontal: ms(20),
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    marginTop: ms(5),
    marginBottom: ms(5),
  },
  closeMenuButton: {
    position: 'absolute',
    top: ms(10),
    right: ms(10),
    zIndex: 10,
  },
  closeMenuText: {
    color: '#fff',
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
  },
  menuLogo: {
    width: s(120),
    height: s(120),
    alignSelf: 'center',
    marginTop: ms(10),
    marginBottom: ms(20),
  },
  menuLinksContainer: {
    marginTop: ms(10),
  },
  menuLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: ms(18),
  },
  menuIconTeal: {
    width: s(28),
    height: s(28),
    marginRight: ms(14),
    resizeMode: 'contain',
    tintColor: '#29BFC0',
  },
  sideMenuText: {
    color: '#fff',
    fontSize: ms(20, 0.3),
    fontWeight: '500',
  },
});

export default ProfileScreen;
